import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import CustomButton from './common/CustomButton';
import { useNavigate } from 'react-router-dom';

// Import des images
import hero1 from '../assets/images/hero/1.jpg';
import hero2 from '../assets/images/hero/2.jpg';
import hero3 from '../assets/images/hero/3.jpg';
import hero4 from '../assets/images/hero/4.jpg';

const HeroWrapper = styled(Box)(() => ({
  width: '100%',
  backgroundColor: '#FFFFFF',
}));

const HeroContainer = styled(Box)(() => ({
  position: 'relative',
  height: '600px',
  '@media (max-width: 600px)': {
    height: '50vh',
    minHeight: '400px',
    maxHeight: '500px',
  },
  overflow: 'hidden',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(180deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.4) 100%)',
    zIndex: 1,
  },
}));

const SlideImage = styled('div')(({ image }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundImage: `url(${image})`,
  backgroundPosition: 'center',
  backgroundSize: 'cover',
  filter: 'saturate(1.1) brightness(1.1)',
  transition: 'all 0.8s ease-in-out',
  transform: 'scale(1.05)',
  '@media (max-width: 600px)': {
    backgroundPosition: 'center 30%',
  },
  '&.active': {
    transform: 'scale(1)',
  },
}));

const SlideIndicator = styled(Box)(({ active }) => ({
  width: '40px',
  height: '4px',
  backgroundColor: active ? '#FFFFFF' : 'rgba(255, 255, 255, 0.3)',
  margin: '0 4px',
  cursor: 'pointer',
  transition: 'all 0.4s ease',
  transform: active ? 'scaleX(1.2)' : 'scaleX(1)',
  '&:hover': {
    backgroundColor: active ? '#FFFFFF' : 'rgba(255, 255, 255, 0.5)',
  },
}));

const TextContainer = styled(Box)(() => ({
  maxWidth: '1200px',
  opacity: 0,
  transform: 'translateY(20px)',
  animation: 'fadeInUp 0.8s ease forwards',
  '@keyframes fadeInUp': {
    '0%': {
      opacity: 0,
      transform: 'translateY(20px)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
}));

const MainTitle = styled(Typography)(({ theme }) => ({
  fontSize: '6rem',
  fontWeight: 700,
  color: '#FFFFFF',
  lineHeight: 1.2,
  marginBottom: '1.5rem',
  '@media (max-width: 900px)': {
    fontSize: '2rem',
  },
  '@media (max-width: 600px)': {
    fontSize: '1.75rem',
  },
}));

const SubTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.5rem',
  fontWeight: 600,
  color: 'rgba(255, 255, 255, 0.95)',
  marginBottom: '1rem',
  '@media (max-width: 900px)': {
    fontSize: '1.25rem',
  },
  '@media (max-width: 600px)': {
    fontSize: '1.1rem',
  },
}));

const Description = styled(Typography)(({ theme }) => ({
  fontSize: '1.1rem',
  fontWeight: 400,
  color: 'rgba(255, 255, 255, 0.9)',
  lineHeight: 1.7,
  marginBottom: '2rem',
  '@media (max-width: 900px)': {
    fontSize: '1rem',
  },
  '@media (max-width: 600px)': {
    fontSize: '0.95rem',
  },
}));

const Hero = () => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const slides = [
    { image: hero1 },
    { image: hero2 },
    { image: hero3 },
    { image: hero4 },
  ];

  const heroContent = {
    title: "Des laboratoires Leaders en Métrologie",
    subtitle: "La précision au service de votre performance",
    description: "Des solutions de mesure d'étalonnage accrédités"
  };

  useEffect(() => {
    const timer = setInterval(() => {
      handleNextSlide();
    }, 6000);

    return () => clearInterval(timer);
  }, [currentSlide]);

  const handleNextSlide = () => {
    if (!isTransitioning) {
      setIsTransitioning(true);
      setCurrentSlide((prev) => (prev + 1) % slides.length);
      setTimeout(() => setIsTransitioning(false), 600);
    }
  };

  const handleSlideClick = (index) => {
    if (!isTransitioning && index !== currentSlide) {
      setIsTransitioning(true);
      setCurrentSlide(index);
      setTimeout(() => setIsTransitioning(false), 800);
    }
  };

  return (
    <HeroWrapper>
      <HeroContainer>
        {slides.map((slide, index) => (
          <SlideImage
            key={index}
            image={slide.image}
            className={currentSlide === index ? 'active' : ''}
            style={{
              opacity: currentSlide === index ? 1 : 0,
              zIndex: currentSlide === index ? 0 : -1,
            }}
          />
        ))}
        
        <Container 
          maxWidth="xl" 
          sx={{ 
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            position: 'relative',
            zIndex: 2,
            px: { xs: 2, sm: 4, md: 8, lg: 12 },
          }}
        >
          <TextContainer>
            <MainTitle>
              {heroContent.title}
            </MainTitle>
            <SubTitle>
              {heroContent.description}
            </SubTitle>
            
            <Stack 
              direction="row" 
              spacing={2}
              sx={{ 
                mt: { xs: 3, sm: 4 },
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 2, sm: 2 }
              }}
            >
              <CustomButton
                onClick={() => navigate('/who-we-are')}
                variant="contained"
                size="large"
              >
                En savoir plus
              </CustomButton>
              <CustomButton
                onClick={() => navigate('/contact')}
                variant="outlined"
                size="large"
                sx={{
                  backgroundColor: 'transparent',
                  borderColor: '#FFFFFF',
                  color: '#FFFFFF',
                  borderWidth: '2px',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    borderColor: '#FFFFFF',
                    borderWidth: '2px',
                    color: '#FFFFFF'
                  }
                }}
              >
                Contactez-nous
              </CustomButton>
            </Stack>
          </TextContainer>
        </Container>

        {/* Indicateurs de slide */}
        <Box
          sx={{
            position: 'absolute',
            bottom: '2rem',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 2,
            display: 'flex',
            gap: 1,
          }}
        >
          {slides.map((_, index) => (
            <SlideIndicator
              key={index}
              active={currentSlide === index}
              onClick={() => handleSlideClick(index)}
            />
          ))}
        </Box>
      </HeroContainer>
    </HeroWrapper>
  );
};

export default Hero;
